import React, { useEffect, useState, useContext, createContext, ReactNode } from 'react';
import firebase from 'firebase/app';
import { Session, User } from '@/types/user.types';

// Create context with proper typing
export const UserContext = createContext<Session>({
  user: null,
  loading: true,
  isAdmin: false,
  isDoctor: false,
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session>({
    user: null,
    loading: true,
    isAdmin: false,
    isDoctor: false,
  });

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (firebaseUser) => {
      let isAdmin = false;
      let isDoctor = false;

      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdTokenResult();
          isAdmin = token.claims.admin === true;
          isDoctor = token.claims.doctor === true;
        } catch (error) {
          console.error('Error getting user token:', error);
        }
      }

      // Map firebase.User to our User type (if needed)
      // For now, we'll cast it directly since they're compatible
      const user = firebaseUser as unknown as User | null;

      setSession({ loading: false, user, isAdmin, isDoctor });
    });

    return () => unsubscribe();
  }, []);

  return (
    <UserContext.Provider value={session}>
      {!session.loading && children}
    </UserContext.Provider>
  );
};

/**
 * Custom hook to access user session
 * @returns Current user session with authentication state
 */
export const useSession = (): Session => {
  const session = useContext(UserContext);
  
  if (session === undefined) {
    throw new Error('useSession must be used within a UserProvider');
  }
  
  return session;
};
